import React from 'react';
import { RADIUS, TAP, useTheme, type Theme } from '../../theme';
import { baseButton } from './shared';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Helt runda hörn (marknadsplatsens pill-knappar). */
  pill?: boolean;
  /** Full bredd. */
  block?: boolean;
  /** Ikon före texten, t.ex. `<Icon as={Plus} />`. */
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** Visar en spinner och spärrar knappen. */
  loading?: boolean;
  style?: React.CSSProperties;
};

function variantStyle(T: Theme, v: ButtonVariant): React.CSSProperties {
  switch (v) {
    case 'primary': return { background: T.accentFill, color: T.onAccent };
    case 'secondary': return { background: T.surface, color: T.text, borderColor: T.borderHover };
    case 'ghost': return { background: 'transparent', color: T.text };
    case 'danger': return { background: T.dangerSoft, color: T.danger };
  }
}

// sm är bara 36 px hög: använd den i täta rader på desktop, aldrig som enda väg till en handling på mobil.
const SIZES: Record<ButtonSize, React.CSSProperties> = {
  sm: { minHeight: 36, padding: '0 14px', fontSize: 14, fontWeight: 700 },
  md: { minHeight: TAP, padding: '0 18px', fontSize: 15, fontWeight: 700 },
  lg: { minHeight: 52, padding: '0 22px', fontSize: 16, fontWeight: 800 },
};

/** Knapp i fyra varianter: primary (accent), secondary (kontur), ghost och danger. */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', pill, block, icon, iconRight, loading, disabled, style, children, type = 'button', ...rest }, ref,
) {
  const T = useTheme();
  const off = disabled || loading;
  return (
    <button
      ref={ref} type={type} disabled={off} aria-busy={loading || undefined} {...rest}
      style={{
        ...baseButton, ...SIZES[size], ...variantStyle(T, variant),
        borderRadius: pill ? RADIUS.pill : RADIUS.md,
        width: block ? '100%' : undefined,
        opacity: off ? 0.55 : 1, cursor: off ? 'default' : 'pointer',
        transition: 'opacity .15s, background .15s',
        ...style,
      }}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {iconRight}
    </button>
  );
});

function Spinner() {
  return <span aria-hidden style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'spin .8s linear infinite', flexShrink: 0 }} />;
}

type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'aria-label'> & {
  /** Krävs: läses upp av skärmläsare och visas som tooltip. */
  label: string;
  /** plain = genomskinlig, filled = surface2, float = surface med skugga (över kartan), primary = accent. */
  variant?: 'plain' | 'filled' | 'float' | 'primary';
  /** Sida i px, minst 44. */
  size?: number;
  /** round = cirkel, annars radie 14. */
  round?: boolean;
  style?: React.CSSProperties;
};

/** Ikonknapp med minst 44×44 tryckyta. */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'plain', size = TAP, round = true, style, children, type = 'button', ...rest }, ref,
) {
  const T = useTheme();
  const side = Math.max(TAP, size);
  const look: Record<string, React.CSSProperties> = {
    plain: { background: 'transparent', color: T.text },
    filled: { background: T.surface2, color: T.text },
    float: { background: T.surface, color: T.text, boxShadow: T.shadow.float },
    primary: { background: T.accentFill, color: T.onAccent },
  };
  return (
    <button
      ref={ref} type={type} aria-label={label} title={label} {...rest}
      style={{
        ...baseButton, ...look[variant], width: side, height: side, padding: 0, flexShrink: 0,
        borderRadius: round ? RADIUS.pill : 14, opacity: rest.disabled ? 0.55 : 1, ...style,
      }}
    >
      {children}
    </button>
  );
});
